import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query'
import useAxiosPrivate from '../../api/hooks/useAxiosPrivate'
import { SupportRequestWithMessages } from '../../utils/types/supportTypes'
import { SUPPORT_REQUESTS_LIST_QUERY_KEY } from './useFetchSupportRequests'
import { SUPPORT_UNREAD_COUNTER_QUERY_KEY } from './useUnreadSupportCounter'
import { useAuthContext } from '../../context/auth/AuthContextProvider'
import { canWriteSupportRequest } from '../../utils/helpers/supportHelper'

export const supportRequestQueryKey = (supportRequestId: number) => [
  'supportRequest',
  supportRequestId,
]

export const invalidateSupportRequest = (
  queryClient: QueryClient,
  supportRequestId: number
) => {
  queryClient.invalidateQueries({
    queryKey: supportRequestQueryKey(supportRequestId),
  })
  queryClient.invalidateQueries({ queryKey: SUPPORT_REQUESTS_LIST_QUERY_KEY })
  queryClient.invalidateQueries({ queryKey: SUPPORT_UNREAD_COUNTER_QUERY_KEY })
}

const useFetchSupportRequestById = (supportRequestId: number) => {
  const axiosPrivate = useAxiosPrivate()
  const queryClient = useQueryClient()
  const { user } = useAuthContext()

  const { data, isPending, error } = useQuery({
    queryKey: supportRequestQueryKey(supportRequestId),
    queryFn: async () => {
      const response = await axiosPrivate.get(
        `/api/support-requests/${supportRequestId}`
      )
      const supportRequest = response.data as SupportRequestWithMessages

      if (
        !supportRequest.unread ||
        !canWriteSupportRequest(user, supportRequest)
      ) {
        return supportRequest
      }

      await axiosPrivate.patch(`/api/support-requests/${supportRequestId}`, {
        markRead: true,
      })

      queryClient.invalidateQueries({
        queryKey: SUPPORT_REQUESTS_LIST_QUERY_KEY,
      })
      queryClient.invalidateQueries({
        queryKey: SUPPORT_UNREAD_COUNTER_QUERY_KEY,
      })

      return { ...supportRequest, unread: false }
    },
    retry: 0,
    gcTime: 0,
    enabled: supportRequestId > 0,
  })

  return {
    supportRequest: data,
    isPending,
    error,
  } as const
}

export default useFetchSupportRequestById
